// Cosmetic day/night cycle animating ambient + directional lights.
// Cycle duration ~5 minutes. No gameplay effect. Toggleable in Settings →
// Graphics (showDayNight) — when off, the lights hold a fixed noon.
//
// dayNightState is a module singleton (boardScale/windTime pattern): the
// NightLights overlay reads sunHeight each frame to drive torch/campfire
// glow opacity without a React re-render per frame.
import { useFrame } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import type * as THREE from 'three';

const CYCLE_DURATION = 240; // seconds — night → night, purely cosmetic (calendar days tick independently)

export const dayNightState = { sunHeight: 1 };

/** 0 in full daylight, →1 through the night half (smoothstepped). */
export function nightFactor(sunHeight: number): number {
  const t = Math.min(1, Math.max(0, (0.15 - sunHeight) / 0.55));
  return t * t * (3 - 2 * t);
}

export function DayNightLighting({ enabled = true }: { enabled?: boolean }) {
  const ambientRef = useRef<THREE.AmbientLight>(null);
  const sunRef = useRef<THREE.DirectionalLight>(null);
  // Shadow camera must cover the whole playable area; the default Three.js
  // OrthographicCamera bounds are ±5, which leaves ~80% of the ~30-unit map
  // outside the shadow frustum. Areas outside clamp to the shadow map edge
  // values, which the renderer interprets as "in shadow" — producing a dark
  // mass in the middle of the map (#122, audit 2026-07-16). Applied per-frame
  // because r3f primitive refs are not populated when the post-mount
  // useEffect first runs.
  const shadowFrustumApplied = useRef(false);

  // When the cycle is disabled, hold fixed noon (bright, neutral) once.
  useEffect(() => {
    if (enabled) return;
    dayNightState.sunHeight = 1;
    if (ambientRef.current) ambientRef.current.intensity = 0.75;
    if (sunRef.current) {
      sunRef.current.intensity = 1.2;
      sunRef.current.color.setRGB(1, 1, 1);
      sunRef.current.position.set(20, 30, 10);
    }
  }, [enabled]);

  useFrame(({ clock }) => {
    if (!enabled) return;
    const t = clock.elapsedTime % CYCLE_DURATION;
    const phase = t / CYCLE_DURATION; // 0..1
    const sunAngle = phase * Math.PI * 2;

    if (sunRef.current && !shadowFrustumApplied.current) {
      const cam = sunRef.current.shadow.camera as THREE.OrthographicCamera;
      cam.left = -20;
      cam.right = 20;
      cam.top = 20;
      cam.bottom = -20;
      cam.near = 0.5;
      cam.far = 200;
      cam.updateProjectionMatrix();
      shadowFrustumApplied.current = true;
    }

    // Sun height: -1 at midnight, 0 at dawn/dusk, 1 at noon.
    // Floor at 0.3 so the map stays legible through the night half of
    // the cycle — the day-night animation is cosmetic, not gameplay-
    // gated, and a floor of 0 was making the single-player shell read
    // as a black void on first boot (#122, audit 2026-07-16).
    const sunHeight = Math.sin(sunAngle);
    const dayFactor = Math.max(0.3, sunHeight);

    // Ambient: brighter floor so the map is legible at night too. The
    // day-night cycle is cosmetic — gameplay must remain visible.
    if (ambientRef.current) {
      ambientRef.current.intensity = 0.5 + 0.25 * dayFactor;
    }

    // Directional (sun): intensity and color temperature. Even at
    // "midnight" the directional stays at 0.5 so terrain reads.
    if (sunRef.current) {
      sunRef.current.intensity = 0.5 + 0.7 * dayFactor;

      // Cool at noon (high sun), warm at dawn/dusk (low sun).
      const warmth = 1 - Math.abs(sunHeight); // 0 at noon, 1 at horizon
      const r = 1.0;
      const g = 0.88 + warmth * 0.12;
      const b = 0.7 + warmth * 0.3;
      sunRef.current.color.setRGB(r, g, b);

      // Move sun across the sky.
      const dist = 35;
      sunRef.current.position.set(
        Math.cos(sunAngle) * dist,
        5 + Math.max(0, sunHeight) * 25,
        Math.sin(sunAngle) * dist * 0.5,
      );
    }
  });

  return (
    <>
      <ambientLight ref={ambientRef} intensity={0.45} />
      <hemisphereLight args={['#a0c4ff', '#6b5544', 0.35]} />
      <directionalLight
        ref={sunRef}
        position={[20, 30, 10]}
        intensity={1.2}
        castShadow={false}
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
    </>
  );
}
